import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ChevronDown,
  ChevronUp,
  Search,
  Users,
  BarChart,
  FileText,
  Trash2,
  Eye,
  Globe,
  RefreshCw,
  Filter
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import UserCreditsManagement from './UserCreditsManagement';
import QueueManagement from './QueueManagement';
// Import the fonts CSS file
import '../../../src/fonts.css';
import EventManagement from '../events/EventManagement';
import { API_BASE_URL } from '../../config/api';

// List of admin UIDs - you should store these in your Firestore database in a separate collection
const ADMIN_UIDS = [
  // Add your admin UIDs here
  "iMTsoOPrg6eR4rlJmaL1f0pA07Y2"
];

const Admin = () => {
  const [allAnalyses, setAllAnalyses] = useState([]);
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState('desc');
  const [activeUserId, setActiveUserId] = useState(null);
  const [activeCountry, setActiveCountry] = useState(null);
  const [countries, setCountries] = useState([]);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [filterAnalysisType, setFilterAnalysisType] = useState('all');
  
  const { currentUser, getIdToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user is admin
  useEffect(() => {
    const checkAdmin = async () => {
      if (!currentUser || !ADMIN_UIDS.includes(currentUser.uid)) {
        // Redirect non-admin users
        navigate('/');
      }
    };

    checkAdmin();
  }, [currentUser, navigate]);

  // Fetch analyses on mount and when navigating back from detail view
  useEffect(() => {
    fetchAllAnalyses();
  }, []);

  // Helper function to update a specific analysis in the list (for immediate UI feedback)
  const updateAnalysisInList = (analysisId, updates) => {
    console.log('=== Admin updateAnalysisInList ===');
    console.log('analysisId:', analysisId, 'updates:', updates);

    setAllAnalyses(prevAnalyses =>
      prevAnalyses.map(analysis =>
        analysis.id === analysisId
          ? { ...analysis, ...updates }
          : analysis
      )
    );
  };

  // Refresh when returning from detail page (location change)
  useEffect(() => {
    // If we're on the main admin page (not a detail page), refresh data
    if (location.pathname === '/admin' || location.pathname === '/admin/') {
      // Check if there's an updated analysis in sessionStorage from the detail view
      const storedAnalysis = sessionStorage.getItem('adminViewAnalysis');
      if (storedAnalysis) {
        try {
          const parsedAnalysis = JSON.parse(storedAnalysis);
          console.log('Found stored analysis in sessionStorage:', parsedAnalysis);

          // If it's marked as processing, update the local state immediately
          if (!parsedAnalysis.processed || parsedAnalysis.status === 'processing') {
            console.log('Updating local state with processing status');
            updateAnalysisInList(parsedAnalysis.id, {
              processed: parsedAnalysis.processed,
              status: parsedAnalysis.status
            });
          }
        } catch (e) {
          console.error('Failed to parse stored analysis', e);
        }
      }

      // Always fetch fresh data from server
      fetchAllAnalyses();
    }
  }, [location.pathname]);

  // Refresh data when component regains visibility (returning from detail page)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchAllAnalyses();
      }
    };

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchAllAnalyses = async () => {
    try {
      setLoading(true);
      
      // Get authentication token
      const token = await getIdToken();
      
      if (!token) {
        throw new Error('Authentication required');
      }
      
      const response = await fetch(`${API_BASE_URL}/admin/analyses`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setAllAnalyses(data);
      
      // Group users and extract countries
      const usersMap = {};
      const uniqueCountries = new Set();
      
      data.forEach(analysis => {
        if (analysis.user_id) {
          if (!usersMap[analysis.user_id]) {
            usersMap[analysis.user_id] = {
              id: analysis.user_id,
              email: analysis.user_email || 'Unknown',
              displayName: analysis.user_displayName || 'Unknown User',
              country: analysis.user_country || 'Unknown',
              credits: analysis.user_credits || '0',
              analysisCount: 0
            };
            
            // Add country to unique countries set
            if (analysis.user_country) {
              uniqueCountries.add(analysis.user_country);
            }
          }
          usersMap[analysis.user_id].analysisCount++;
        }
      });
      
      setUsers(usersMap);
      setCountries(Array.from(uniqueCountries).sort());
      setError(null);
    } catch (error) {
      console.error('Error fetching analyses:', error);
      setError('Failed to load analyses: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAnalysis = async (id) => {
    if (!window.confirm('Are you sure you want to delete this analysis?')) {
      return;
    }
    
    try {
      const token = await getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/analyses/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Update local state
      setAllAnalyses(allAnalyses.filter(analysis => analysis.id !== id));
      
      // Update user counts
      const updatedUsers = { ...users };
      const analysisToDelete = allAnalyses.find(a => a.id === id);
      
      if (analysisToDelete && analysisToDelete.user_id && updatedUsers[analysisToDelete.user_id]) {
        updatedUsers[analysisToDelete.user_id].analysisCount--;
        setUsers(updatedUsers);
      }
      
    } catch (error) {
      console.error('Error deleting analysis:', error);
      setError('Failed to delete analysis: ' + error.message);
    }
  };

  const handleViewAnalysis = (analysis) => {
    // Store selected analysis in session storage
    sessionStorage.setItem('adminViewAnalysis', JSON.stringify(analysis));
    navigate(`/admin/analysis/${analysis.id}`);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    
    try {
      const year = timestamp.substring(0, 4);
      const month = timestamp.substring(4, 6);
      const day = timestamp.substring(6, 8);
      const hour = timestamp.substring(8, 10);
      const minute = timestamp.substring(10, 12);
      return `${year}/${month}/${day} ${hour}:${minute}`;
    } catch (e) {
      return timestamp;
    }
  };

  const handleRerunAnalysis = async (analysisId) => {
    if (!window.confirm('Are you sure you want to rerun this analysis?')) {
      return;
    }
    
    try {
      setLoading(true);
      const token = await getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/analyses/${analysisId}/rerun`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to rerun analysis');
      }
      
      // Show success message
      alert('Analysis rerun started successfully');
      
      // Refresh analyses list
      await fetchAllAnalyses();
    } catch (error) {
      console.error('Error rerunning analysis:', error);
      setError('Failed to rerun analysis: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredAnalyses = () => {
    // Filter analyses by search term, active user, active country, and analysis type
    return allAnalyses.filter(analysis => {
      // Filter by active user if set
      if (activeUserId && analysis.user_id !== activeUserId) {
        return false;
      }
      
      // Filter by active country if set
      if (activeCountry && analysis.user_country !== activeCountry) {
        return false;
      }
      
      // Filter by analysis type if set
      if (filterAnalysisType !== 'all' && analysis.analysis_type !== filterAnalysisType) {
        return false;
      }
      
      // Filter by search term
      const searchLower = searchTerm.toLowerCase();
      return (
        (analysis.dance_type && analysis.dance_type.toLowerCase().includes(searchLower)) ||
        (analysis.dancers && analysis.dancers.toLowerCase().includes(searchLower)) ||
        (analysis.user_email && analysis.user_email.toLowerCase().includes(searchLower)) ||
        (analysis.user_displayName && analysis.user_displayName.toLowerCase().includes(searchLower)) ||
        (analysis.user_country && analysis.user_country.toLowerCase().includes(searchLower))
      );
    }).sort((a, b) => {
      // Sort by selected field
      if (sortField === 'timestamp') {
        return sortOrder === 'desc' 
          ? (b.timestamp || '').localeCompare(a.timestamp || '')
          : (a.timestamp || '').localeCompare(b.timestamp || '');
      } else if (sortField === 'user') {
        const aEmail = a.user_email || '';
        const bEmail = b.user_email || '';
        return sortOrder === 'desc' ? bEmail.localeCompare(aEmail) : aEmail.localeCompare(bEmail);
      } else if (sortField === 'dance_type') {
        const aDance = a.dance_type || '';
        const bDance = b.dance_type || '';
        return sortOrder === 'desc' ? bDance.localeCompare(aDance) : aDance.localeCompare(bDance);
      } else if (sortField === 'country') {
        const aCountry = a.user_country || '';
        const bCountry = b.user_country || '';
        return sortOrder === 'desc' ? bCountry.localeCompare(aCountry) : aCountry.localeCompare(bCountry);
      }
      return 0;
    });
  };

  // Get user list sorted by number of analyses
  const getUsersList = () => {
    // Filter users by country if active country is set
    let filteredUsers = Object.values(users);
    
    if (activeCountry) {
      filteredUsers = filteredUsers.filter(user => user.country === activeCountry);
    }
    
    return filteredUsers.sort((a, b) => b.analysisCount - a.analysisCount);
  };

  // Count analyses by type
  const getAnalysisCounts = () => {
    const counts = {
      couple: allAnalyses.filter(a => a.analysis_type === 'couple' || !a.analysis_type).length,
      solo: allAnalyses.filter(a => a.analysis_type === 'solo').length,
      duo: allAnalyses.filter(a => a.analysis_type === 'duo').length,
      formation: allAnalyses.filter(a => a.analysis_type === 'formation').length
    };
    return counts;
  };

  // Handle clearing all filters
  const clearAllFilters = () => {
    setActiveUserId(null);
    setActiveCountry(null);
    setSearchTerm('');
    setFilterAnalysisType('all');
    setShowFilterPanel(false);
  };

  return (
    <div className="h-full">
      <h1 className="text-3xl font-bold text-blue-800 mb-6">
        Admin Dashboard
      </h1>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading...</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Users Sidebar */}
          <div className="md:col-span-1">
            {/* Filter Controls */}
            <div className="bg-white rounded-lg shadow p-4 mb-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                  <Filter className="w-5 h-5 mr-2" />
                  Filters
                </h2>
                <button 
                  onClick={() => setShowFilterPanel(!showFilterPanel)}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  {showFilterPanel ? 'Hide' : 'Show'}
                </button>
              </div>
              
              {showFilterPanel && (
                <div className="space-y-4">
                  {/* Analysis Type Filter */}
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Filter by Analysis Type</p>
                    <div className="space-y-1">
                      <div 
                        onClick={() => setFilterAnalysisType('all')}
                        className={`px-3 py-2 rounded-md cursor-pointer flex items-center justify-between text-sm ${
                          filterAnalysisType === 'all' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex items-center">
                          <FileText className="w-4 h-4 mr-2 opacity-70" />
                          All Types
                        </div>
                      </div>
                      <div 
                        onClick={() => setFilterAnalysisType('couple')}
                        className={`px-3 py-2 rounded-md cursor-pointer flex items-center justify-between text-sm ${
                          filterAnalysisType === 'couple' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex items-center">
                          <Users className="w-4 h-4 mr-2 opacity-70" />
                          Couple Analysis
                        </div>
                        <div className="text-xs bg-gray-200 rounded-full px-2 py-1">
                          {getAnalysisCounts().couple}
                        </div>
                      </div>
                      <div 
                        onClick={() => setFilterAnalysisType('solo')}
                        className={`px-3 py-2 rounded-md cursor-pointer flex items-center justify-between text-sm ${
                          filterAnalysisType === 'solo' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex items-center">
                          <Users className="w-4 h-4 mr-2 opacity-70" />
                          Solo Analysis
                        </div>
                        <div className="text-xs bg-gray-200 rounded-full px-2 py-1">
                          {getAnalysisCounts().solo}
                        </div>
                      </div>
                      <div 
                        onClick={() => setFilterAnalysisType('duo')}
                        className={`px-3 py-2 rounded-md cursor-pointer flex items-center justify-between text-sm ${
                          filterAnalysisType === 'duo' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex items-center">
                          <Users className="w-4 h-4 mr-2 opacity-70" />
                          Duo Analysis
                        </div>
                        <div className="text-xs bg-gray-200 rounded-full px-2 py-1">
                          {getAnalysisCounts().duo}
                        </div>
                      </div>
                      <div 
                        onClick={() => setFilterAnalysisType('formation')}
                        className={`px-3 py-2 rounded-md cursor-pointer flex items-center justify-between text-sm ${
                          filterAnalysisType === 'formation' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex items-center">
                          <Users className="w-4 h-4 mr-2 opacity-70" />
                          Formation Analysis
                        </div>
                        <div className="text-xs bg-gray-200 rounded-full px-2 py-1">
                          {getAnalysisCounts().formation}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Country Filter */}
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Filter by Country</p>
                    <div className="max-h-48 overflow-y-auto">
                      {countries.length > 0 ? (
                        <div className="space-y-1">
                          {countries.map(country => (
                            <div 
                              key={country}
                              onClick={() => setActiveCountry(country === activeCountry ? null : country)}
                              className={`px-3 py-2 rounded-md cursor-pointer flex items-center justify-between text-sm ${
                                country === activeCountry 
                                ? 'bg-blue-100 text-blue-800' 
                                : 'hover:bg-gray-100'
                              }`}
                            >
                              <div className="flex items-center">
                                <Globe className="w-4 h-4 mr-2 opacity-70" />
                                {country}
                              </div>
                              <div className="text-xs bg-gray-200 rounded-full px-2 py-1">
                                {Object.values(users).filter(user => user.country === country).length}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 italic">No countries found</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Clear Filters Button */}
                  <button
                    onClick={clearAllFilters}
                    className="w-full py-2 px-3 border border-gray-300 rounded-md text-sm hover:bg-gray-50"
                  >
                    Clear All Filters
                  </button>
                </div>
              )}
            </div>
            
            {/* Users List */}
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  Users ({getUsersList().length})
                </h2>
                {(activeUserId || activeCountry) && (
                  <button 
                    onClick={() => {
                      setActiveUserId(null);
                      setActiveCountry(null);
                    }}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Show All
                  </button>
                )}
              </div>
              
              <div className="space-y-2 max-h-[calc(100vh-350px)] overflow-y-auto">
                {getUsersList().length > 0 ? (
                  getUsersList().map(user => (
                    <div 
                      key={user.id}
                      onClick={() => setActiveUserId(user.id === activeUserId ? null : user.id)}
                      className={`p-3 rounded-lg cursor-pointer flex items-center justify-between ${
                        user.id === activeUserId 
                          ? 'bg-blue-100 border border-blue-300' 
                          : 'hover:bg-gray-100'
                      }`}
                    >
                      <div className="overflow-hidden">
                        <div className="font-medium truncate">{user.displayName}</div>
                        <div className="text-sm text-gray-500 truncate">{user.email}</div>
                        <div className="text-sm text-blue-500 truncate">CREDITS: {user.credits}</div>
                        <div className="text-xs text-gray-400 flex items-center mt-1">
                          <Globe className="w-3 h-3 mr-1" />
                          {user.country || 'Unknown'}
                        </div>
                      </div>
                      <div className="text-sm bg-gray-200 px-2 py-1 rounded-full">
                        {user.analysisCount}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-gray-500 py-4">
                    No users found
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="md:col-span-3">
            {/* Queue Management Section */}
            <QueueManagement />

            <div className="bg-white rounded-lg shadow p-4 mb-6">
              <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-4">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                  <FileText className="w-5 h-5 mr-2" />
                  Analyses ({getFilteredAnalyses().length})
                  {/* Show active filters indicators */}
                  <div className="flex items-center ml-2 flex-wrap">
                    {activeUserId && users[activeUserId] && (
                      <span className="ml-2 text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded-full flex items-center">
                        User: {users[activeUserId].email}
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveUserId(null);
                          }} 
                          className="ml-1 text-blue-600 hover:text-blue-800"
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {activeCountry && (
                      <span className="ml-2 text-sm bg-green-100 text-green-800 px-2 py-1 rounded-full flex items-center">
                        Country: {activeCountry}
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveCountry(null);
                          }} 
                          className="ml-1 text-green-600 hover:text-green-800"
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {filterAnalysisType !== 'all' && (
                      <span className="ml-2 text-sm bg-purple-100 text-purple-800 px-2 py-1 rounded-full flex items-center">
                        Type: {filterAnalysisType.charAt(0).toUpperCase() + filterAnalysisType.slice(1)}
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setFilterAnalysisType('all');
                          }} 
                          className="ml-1 text-purple-600 hover:text-purple-800"
                        >
                          ×
                        </button>
                      </span>
                    )}
                  </div>
                </h2>
                
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Sort by:</span>
                  <select 
                    value={`${sortField}-${sortOrder}`}
                    onChange={(e) => {
                      const [field, order] = e.target.value.split('-');
                      setSortField(field);
                      setSortOrder(order);
                    }}
                    className="border rounded p-1"
                  >
                    <option value="timestamp-desc">Date (Newest)</option>
                    <option value="timestamp-asc">Date (Oldest)</option>
                    <option value="user-asc">User (A-Z)</option>
                    <option value="user-desc">User (Z-A)</option>
                    <option value="dance_type-asc">Dance Type (A-Z)</option>
                    <option value="dance_type-desc">Dance Type (Z-A)</option>
                    <option value="country-asc">Country (A-Z)</option>
                    <option value="country-desc">Country (Z-A)</option>
                  </select>
                </div>
              </div>
              
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search analyses..."
                  className="w-full p-2 pl-10 border rounded-lg mb-4"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
              </div>
              
              {/* Analysis Statistics */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                  <div className="text-sm text-gray-500">Total Analyses</div>
                  <div className="text-2xl font-semibold">{allAnalyses.length}</div>
                </div>
                
                <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                  <div className="text-sm text-gray-500">Processed</div>
                  <div className="text-2xl font-semibold">
                    {allAnalyses.filter(a => a.processed).length}
                  </div>
                </div>
                
                <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
                  <div className="text-sm text-gray-500">Couple</div>
                  <div className="text-2xl font-semibold">
                    {getAnalysisCounts().couple}
                  </div>
                </div>
                
                <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100">
                  <div className="text-sm text-gray-500">Solo</div>
                  <div className="text-2xl font-semibold">
                    {getAnalysisCounts().solo}
                  </div>
                </div>
                
                <div className="bg-pink-50 rounded-lg p-3 border border-pink-100">
                  <div className="text-sm text-gray-500">Duo/Formation</div>
                  <div className="text-2xl font-semibold">
                    {getAnalysisCounts().duo + getAnalysisCounts().formation}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Analyses Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
            {/* Desktop Table Header */}
            <div className="hidden md:grid grid-cols-7 p-4 font-semibold border-b bg-gray-50">
              <div className="col-span-2">Analysis</div>
              <div>User</div>
              <div>Date</div>
              <div>Country</div>
              <div>Score</div>
              <div className="text-right">Actions</div>
            </div>

              <div className="max-h-[calc(100vh-400px)] overflow-y-auto">
                {getFilteredAnalyses().length > 0 ? (
                  getFilteredAnalyses().map(analysis => (
                    <div
                        key={analysis.id}
                      >
                      {/* Mobile Card Layout */}
                      <div className="md:hidden p-4 border-b hover:bg-gray-50">
                        <div className={`mb-3 ${!analysis.processed ? 'bg-orange-50' : ''}`}>
                          <div className="font-semibold text-base mb-1">{analysis.dance_type || 'Unknown'}</div>
                          <div className="text-sm text-gray-600 mb-2">{analysis.dancers || 'Unknown dancers'}</div>
                          <div className="text-xs text-gray-500 inline-block px-2 py-1 bg-gray-100 rounded mb-2">
                            {analysis.analysis_type === 'solo' ? 'Solo Analysis' :
                            analysis.analysis_type === 'duo' ? 'Duo Analysis' :
                            analysis.analysis_type === 'formation' ? 'Formation Analysis' :
                            'Couple Analysis'}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                          <div>
                            <span className="text-gray-500">User:</span>
                            <div className="text-gray-900">{analysis.user_email || 'Unknown'}</div>
                          </div>
                          <div>
                            <span className="text-gray-500">Date:</span>
                            <div className="text-gray-900">{formatDate(analysis.timestamp)}</div>
                          </div>
                          <div>
                            <span className="text-gray-500">Country:</span>
                            <div className="text-gray-900 flex items-center">
                              <Globe className="w-3 h-3 mr-1 text-gray-400" />
                              {analysis.user_country || 'Unknown'}
                            </div>
                          </div>
                          <div>
                            <span className="text-gray-500">Score:</span>
                            <div className="text-gray-900">
                              {analysis.total_score ? (
                                <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-sm font-semibold">
                                  {analysis.total_score}
                                </span>
                              ) : (
                                <span className="text-gray-400">N/A</span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-1 text-xs mb-3">
                          {analysis.processed ? (
                            <span className="text-green-600">✓ Processed</span>
                          ) : (
                            <span className="text-orange-600">⟳ In Progress</span>
                          )}
                        </div>

                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleViewAnalysis(analysis)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg min-h-[40px] flex items-center"
                          >
                            <Eye size={18} className="mr-1" />
                            View
                          </button>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRerunAnalysis(analysis.id);
                            }}
                            className="px-3 py-2 bg-green-100 text-green-700 rounded-lg min-h-[40px] flex items-center"
                          >
                            <RefreshCw size={18} className="mr-1" />
                            Rerun
                          </button>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteAnalysis(analysis.id);
                            }}
                            className="px-3 py-2 bg-red-100 text-red-700 rounded-lg min-h-[40px] flex items-center"
                          >
                            <Trash2 size={18} className="mr-1" />
                            Delete
                          </button>
                        </div>
                      </div>

                      {/* Desktop Table Row */}
                      <div
                        className={`hidden md:grid grid-cols-7 p-4 border-b hover:bg-gray-50 ${
                          !analysis.processed ? 'bg-orange-50' : ''
                        }`}
                      >
                        <div className="col-span-2">
                          <div className="font-medium">{analysis.dance_type || 'Unknown'}</div>
                          <div className="text-sm text-gray-600">{analysis.dancers || 'Unknown dancers'}</div>
                          <div className="text-xs text-gray-500">
                            {analysis.analysis_type === 'solo' ? 'Solo Analysis' :
                            analysis.analysis_type === 'duo' ? 'Duo Analysis' :
                            analysis.analysis_type === 'formation' ? 'Formation Analysis' :
                            'Couple Analysis'}
                          </div>
                        </div>

                        <div>
                          <div className="text-xs text-gray-600">{analysis.user_email || 'Unknown'}</div>
                          <div className="text-xs text-gray-500">{analysis.user_displayName || 'Unknown User'}</div>

                        </div>

                        <div className="text-sm">
                          {formatDate(analysis.timestamp)}
                          <div className="text-xs text-gray-500">
                            {analysis.processed ? 'Processed' : 'In Progress'}
                          </div>
                        </div>

                        <div className="text-sm flex items-center">
                          <Globe className="w-4 h-4 mr-1 text-gray-400" />
                          {analysis.user_country || 'Unknown'}
                        </div>

                        {/* New Score Column */}
                        <div className="text-sm">
                          {analysis.total_score ? (
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
                              {analysis.total_score}
                            </span>
                          ) : (
                            <span className="text-gray-400">N/A</span>
                          )}
                        </div>

                        <div className="flex justify-end items-center gap-2">
                          <button
                            onClick={() => handleViewAnalysis(analysis)}
                            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                            title="View Analysis"
                          >
                            <Eye size={18} />
                          </button>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRerunAnalysis(analysis.id);
                            }}
                            className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded"
                            title="Rerun Analysis"
                          >
                            <RefreshCw size={18} />
                          </button>

                          <button
                            onClick={() => handleDeleteAnalysis(analysis.id)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                            title="Delete Analysis"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    No analyses found
                  </div>
                )}
              </div>
            </div>
            <div className="md:col-span-4 mt-8">
              <UserCreditsManagement />
              <EventManagement getIdToken={getIdToken} />
            </div>
          </div>
          
        </div>
      )}
    </div>
  );
};

export default Admin;